// ═══════════════════════════════════════════
// BLOQUE 8 — GAMIFICACIÓN TOTAL
// Misiones, Recompensas, Progreso unificado
// ═══════════════════════════════════════════

// ── MISIÓN TYPE ──
export interface GamificationMission {
  id: string;
  title: string;
  description: string;
  points: number;
  progress: number;
  required: number;
  unit: string;
  icon: string;
  color: string;
  completed: boolean;
  period: 'daily' | 'weekly' | 'monthly';
  resetLabel: string;
}

// ── RECOMPENSA TYPE ──
export interface GamificationReward {
  id: string;
  type: 'points' | 'badge' | 'recognition' | 'position';
  name: string;
  description: string;
  value: string;
  icon: string;
  color: string;
  unlocked: boolean;
  unlockCondition: string;
}

// ── PERFIL UNIFICADO ──
export interface UnifiedGamificationProfile {
  name: string;
  username: string;
  photoUrl: string;
  currentLevel: number;
  currentLevelName: string;
  nextLevelName: string;
  totalPoints: number;
  pointsToNextLevel: number;
  progressPercent: number;
  globalRank: number;
  listenersAtLevel: number;
  badgesEarned: number;
  badgesTotal: number;
  streakDays: number;
  todayPoints: number;
  weeklyPoints: number;
  weeklyGoal: number;
}

// ═══════════════════════════════════════════
// MISIONES DIARIAS
// ═══════════════════════════════════════════

export const dailyMissions: GamificationMission[] = [
  {
    id: 'dm1',
    title: 'Escuchá 30 minutos',
    description: 'Sintonizá cualquier radio RFW por al menos 30 minutos seguidos.',
    points: 20,
    progress: 22,
    required: 30,
    unit: 'min',
    icon: 'ri-headphone-line',
    color: 'primary',
    completed: false,
    period: 'daily',
    resetLabel: 'Se reinicia en 4h 32min',
  },
  {
    id: 'dm2',
    title: 'Enviá un saludo',
    description: 'Mandá un saludo en vivo a tu radio favorita durante la transmisión.',
    points: 25,
    progress: 1,
    required: 1,
    unit: 'saludo',
    icon: 'ri-message-2-line',
    color: 'accent',
    completed: true,
    period: 'daily',
    resetLabel: 'Se reinicia en 4h 32min',
  },
  {
    id: 'dm3',
    title: 'Compartí contenido',
    description: 'Compartí cualquier programa, post o momento en vivo en tus redes.',
    points: 15,
    progress: 1,
    required: 1,
    unit: 'compartido',
    icon: 'ri-share-forward-line',
    color: 'emerald',
    completed: true,
    period: 'daily',
    resetLabel: 'Se reinicia en 4h 32min',
  },
  {
    id: 'dm4',
    title: 'Comentá 2 publicaciones',
    description: 'Dejá tu comentario en al menos 2 publicaciones de radios o locutores.',
    points: 10,
    progress: 1,
    required: 2,
    unit: 'comentario',
    icon: 'ri-chat-1-line',
    color: 'sky',
    completed: false,
    period: 'daily',
    resetLabel: 'Se reinicia en 4h 32min',
  },
  {
    id: 'dm5',
    title: 'Reaccioná a 3 posts',
    description: 'Dale like o reacción a 3 publicaciones de la comunidad.',
    points: 15,
    progress: 3,
    required: 3,
    unit: 'reacciones',
    icon: 'ri-heart-line',
    color: 'rose',
    completed: true,
    period: 'daily',
    resetLabel: 'Se reinicia en 4h 32min',
  },
];

// ═══════════════════════════════════════════
// MISIONES SEMANALES
// ═══════════════════════════════════════════

export const weeklyMissions: GamificationMission[] = [
  {
    id: 'wm1',
    title: 'Escuchá 5 horas',
    description: 'Acumulá 5 horas de escucha en cualquier radio de la red.',
    points: 150,
    progress: 3.2,
    required: 5,
    unit: 'horas',
    icon: 'ri-headphone-line',
    color: 'primary',
    completed: false,
    period: 'weekly',
    resetLabel: 'Se reinicia en 3 días',
  },
  {
    id: 'wm2',
    title: 'Visitá 3 radios diferentes',
    description: 'Explorá al menos 3 emisoras distintas durante la semana.',
    points: 100,
    progress: 2,
    required: 3,
    unit: 'radios',
    icon: 'ri-radio-line',
    color: 'amber',
    completed: false,
    period: 'weekly',
    resetLabel: 'Se reinicia en 3 días',
  },
  {
    id: 'wm3',
    title: 'Participá en un concurso',
    description: 'Participá en cualquier concurso activo de una radio RFW.',
    points: 200,
    progress: 1,
    required: 1,
    unit: 'concurso',
    icon: 'ri-trophy-line',
    color: 'orange',
    completed: true,
    period: 'weekly',
    resetLabel: 'Se reinicia en 3 días',
  },
  {
    id: 'wm4',
    title: 'Invitá a un amigo',
    description: 'Compartí tu código de referido y que un amigo se registre.',
    points: 250,
    progress: 0,
    required: 1,
    unit: 'invitado',
    icon: 'ri-user-add-line',
    color: 'violet',
    completed: false,
    period: 'weekly',
    resetLabel: 'Se reinicia en 3 días',
  },
  {
    id: 'wm5',
    title: 'Completá 5 misiones diarias',
    description: 'Completá al menos 5 misiones diarias en la semana.',
    points: 300,
    progress: 3,
    required: 5,
    unit: 'misiones',
    icon: 'ri-check-double-line',
    color: 'emerald',
    completed: false,
    period: 'weekly',
    resetLabel: 'Se reinicia en 3 días',
  },
];

// ═══════════════════════════════════════════
// MISIONES MENSUALES
// ═══════════════════════════════════════════

export const monthlyMissions: GamificationMission[] = [
  {
    id: 'mm1',
    title: 'Escuchá 20 horas',
    description: 'Acumulá 20 horas de escucha en el mes.',
    points: 500,
    progress: 12.5,
    required: 20,
    unit: 'horas',
    icon: 'ri-headphone-line',
    color: 'primary',
    completed: false,
    period: 'monthly',
    resetLabel: 'Se reinicia en 18 días',
  },
  {
    id: 'mm2',
    title: 'Asistí a un evento',
    description: 'Registrate y asistí a un evento de cualquier radio RFW.',
    points: 300,
    progress: 0,
    required: 1,
    unit: 'evento',
    icon: 'ri-calendar-check-line',
    color: 'rose',
    completed: false,
    period: 'monthly',
    resetLabel: 'Se reinicia en 18 días',
  },
  {
    id: 'mm3',
    title: 'Unite a un Club',
    description: 'Unite a un club oficial de radio, programa o locutor.',
    points: 400,
    progress: 1,
    required: 1,
    unit: 'club',
    icon: 'ri-group-line',
    color: 'amber',
    completed: true,
    period: 'monthly',
    resetLabel: 'Se reinicia en 18 días',
  },
  {
    id: 'mm4',
    title: 'Ganá un concurso',
    description: 'Salí ganador en cualquier concurso de la red RFW.',
    points: 800,
    progress: 0,
    required: 1,
    unit: 'victoria',
    icon: 'ri-medal-line',
    color: 'orange',
    completed: false,
    period: 'monthly',
    resetLabel: 'Se reinicia en 18 días',
  },
  {
    id: 'mm5',
    title: 'Pedí 5 canciones',
    description: 'Solicitá al menos 5 canciones al aire en distintas radios.',
    points: 350,
    progress: 3,
    required: 5,
    unit: 'canciones',
    icon: 'ri-music-line',
    color: 'sky',
    completed: false,
    period: 'monthly',
    resetLabel: 'Se reinicia en 18 días',
  },
];

// ═══════════════════════════════════════════
// RECOMPENSAS
// ═══════════════════════════════════════════

export const rewardsCatalog: GamificationReward[] = [
  {
    id: 'rw1',
    type: 'points',
    name: 'Puntos de Escucha',
    description: 'Cada 30 minutos de escucha continua sumás puntos automáticamente.',
    value: '+10 pts / 30min',
    icon: 'ri-copper-coin-line',
    color: 'amber',
    unlocked: true,
    unlockCondition: 'Desde Nivel 1',
  },
  {
    id: 'rw2',
    type: 'points',
    name: 'Bonus de Racha',
    description: 'Por cada día consecutivo que entrás a RFW, ganás puntos extra.',
    value: '+5 pts × día',
    icon: 'ri-fire-line',
    color: 'orange',
    unlocked: true,
    unlockCondition: 'Racha activa: Día 5',
  },
  {
    id: 'rw3',
    type: 'badge',
    name: 'Insignia Primer Saludo',
    description: 'Tu primera interacción en vivo con una radio queda marcada para siempre.',
    value: 'Desbloqueada',
    icon: 'ri-message-2-line',
    color: 'emerald',
    unlocked: true,
    unlockCondition: 'Enviar 1 saludo',
  },
  {
    id: 'rw4',
    type: 'badge',
    name: 'Insignia 100 Horas',
    description: '100 horas de escucha acumuladas en radios RFW.',
    value: 'Desbloqueada',
    icon: 'ri-timer-line',
    color: 'primary',
    unlocked: true,
    unlockCondition: '100 horas de escucha',
  },
  {
    id: 'rw5',
    type: 'recognition',
    name: 'Top Fan del Mes',
    description: 'Reconocimiento público en la homepage y mención al aire por ser el más activo.',
    value: 'Homepage + Al Aire',
    icon: 'ri-star-line',
    color: 'amber',
    unlocked: false,
    unlockCondition: 'Ser #1 en tu radio',
  },
  {
    id: 'rw6',
    type: 'recognition',
    name: 'Embajador Verificado',
    description: 'Perfil verificado permanente con badge dorado vitalicio.',
    value: 'Badge Dorado',
    icon: 'ri-verified-badge-line',
    color: 'rose',
    unlocked: false,
    unlockCondition: 'Nivel 6: 15,000 pts',
  },
  {
    id: 'rw7',
    type: 'position',
    name: 'Podium Semanal',
    description: 'Tu nombre aparece en el top 3 del ranking semanal de tu radio.',
    value: 'Top 3 Semanal',
    icon: 'ri-medal-line',
    color: 'orange',
    unlocked: false,
    unlockCondition: 'Top 3 en tu radio',
  },
  {
    id: 'rw8',
    type: 'position',
    name: 'Salón de la Fama',
    description: 'Entrada permanente al Hall of Fame de competencias RFW.',
    value: 'Hall of Fame',
    icon: 'ri-trophy-line',
    color: 'violet',
    unlocked: false,
    unlockCondition: 'Ganar torneo anual',
  },
];

// ═══════════════════════════════════════════
// PERFIL UNIFICADO
// ═══════════════════════════════════════════

export const unifiedProfile: UnifiedGamificationProfile = {
  name: 'Marco Antonio Rivera',
  username: '@marco_oyente',
  photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Latino%20man%20in%20his%20late%2020s%20with%20warm%20confident%20smile%2C%20dark%20hair%2C%20studio%20portrait%20with%20clean%20minimal%20neutral%20background%2C%20editorial%20portrait%20photography%20with%20soft%20natural%20lighting&width=200&height=200&seq=unified-profile&orientation=squarish',
  currentLevel: 4,
  currentLevelName: 'Top Fan',
  nextLevelName: 'VIP',
  totalPoints: 4850,
  pointsToNextLevel: 6000,
  progressPercent: 67,
  globalRank: 4231,
  listenersAtLevel: 847,
  badgesEarned: 5,
  badgesTotal: 8,
  streakDays: 5,
  todayPoints: 75,
  weeklyPoints: 385,
  weeklyGoal: 500,
};

// ═══════════════════════════════════════════
// STATS GLOBALES DE GAMIFICACIÓN
// ═══════════════════════════════════════════

export const gamificationGlobalStats = {
  totalListenersGamified: 42310,
  missionsCompletedToday: 18742,
  totalPointsAwardedToday: 486200,
  activeStreaks: 12453,
  avgMissionsPerListener: 8.4,
  topMissionCategory: 'Escucha',
  globalBadgesEarned: 89200,
  highestLevelCount: 28,
};

// ═══════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════

export function getMissionsByPeriod(period: 'daily' | 'weekly' | 'monthly'): GamificationMission[] {
  switch (period) {
    case 'daily': return dailyMissions;
    case 'weekly': return weeklyMissions;
    case 'monthly': return monthlyMissions;
  }
}

export function getMissionProgress(missions: GamificationMission[]): { completed: number; total: number; percent: number; totalPoints: number; earnedPoints: number } {
  const completed = missions.filter((m) => m.completed).length;
  const total = missions.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const totalPoints = missions.reduce((sum, m) => sum + m.points, 0);
  const earnedPoints = missions.filter((m) => m.completed).reduce((sum, m) => sum + m.points, 0);
  return { completed, total, percent, totalPoints, earnedPoints };
}

export function getTotalDailyPoints(missions: GamificationMission[]): number {
  return missions.filter((m) => m.completed).reduce((sum, m) => sum + m.points, 0);
}

export const missionPeriodLabels: Record<string, { icon: string; label: string; color: string }> = {
  daily: { icon: 'ri-sun-line', label: 'Diarias', color: 'text-amber-500' },
  weekly: { icon: 'ri-calendar-line', label: 'Semanales', color: 'text-emerald-500' },
  monthly: { icon: 'ri-calendar-2-line', label: 'Mensuales', color: 'text-rose-500' },
};

export const rewardTypeLabels: Record<string, { icon: string; label: string }> = {
  points: { icon: 'ri-copper-coin-line', label: 'Puntos' },
  badge: { icon: 'ri-award-line', label: 'Insignia' },
  recognition: { icon: 'ri-star-line', label: 'Reconocimiento' },
  position: { icon: 'ri-medal-line', label: 'Posición' },
};